import { memo, useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { ImageOff } from 'lucide-react';

import Badge from '../../../Badge.jsx';
import { WidgetCarouselNav } from '../shared';

/**
 * The space between two cards, in pixels — `gap-2` on the track below.
 *
 * Named because three places have to agree on it: the class that draws it,
 * the card width that subtracts the gaps inside one page, and the scroll
 * step that has to clear the gap *between* two pages.
 */
const CARD_GAP = 8;

/**
 * A few rich items, browsed sideways.
 *
 * ## Reading the contract (CT-5641)
 *
 * `CAROUSEL` publishes `items[{row_key, title, subtitle, badge, image_url}]`.
 *
 * - **`image_url`** is the reason a carousel exists — a carousel of catalogue
 *   items drawn as four identical glyphs is a list with extra scrolling.
 * - **`badge`** sits **on** the image, top left. It is a product label
 *   ("-15%", "Best seller"), and a label under the price reads as another
 *   line of prose.
 *
 * ## The picture that is not there
 *
 * `image_url` is nullable and the contract's own example sends `null` for
 * every item, so the missing picture is the common case, not the edge. There
 * are two of them and they are different:
 *
 * - **No URL at all.** Nothing was ever bound; the card shows a plain
 *   placeholder and gets on with it.
 * - **A URL that fails.** A dead CDN, an expired signature, a file someone
 *   moved. Left alone the browser paints its own broken-image glyph, which
 *   looks like the widget is broken rather than the picture. `onError` folds
 *   it into the same placeholder — the card still reads, and nothing claims a
 *   failure the reader can do anything about.
 *
 * The image strip is a fixed height with `object-cover`: a catalogue's photos
 * arrive in whatever aspect their supplier uploaded, and letting them set the
 * card height makes every card a different size.
 *
 * ## Paging
 *
 * `items_per_view` cards at a time, with arrows and a count. This used to
 * crop and dim the last card instead, which says "there is more" without
 * costing a tap target — fine for a 240px slot, but it cannot say *how much*
 * more, and the contract's `display.items_per_view` is an explicit page size.
 *
 * @param {object} props
 * @param {{ items: object[] }} props.data `CAROUSEL`'s `items[]`.
 * @param {number} [props.itemsPerView=3] The contract's `display.items_per_view`.
 * @param {(item: object) => void} [props.onItemClick]
 * @param {string} [props.className]
 */
function CarouselWidget({ data, itemsPerView = 3, onItemClick, className = '' }) {
  const items = useMemo(() => (data?.items ?? []).map((item, index) => ({
    key: item.row_key ?? item.id ?? String(index),
    title: item.title,
    subtitle: item.subtitle ?? item.meta ?? '',
    badge: item.badge ?? null,
    imageUrl: item.image_url ?? null,
    raw: item,
  })), [data]);

  const trackRef = useRef(null);
  const [page, setPage] = useState(0);
  /** Keys whose `image_url` did not load. See the note above. */
  const [broken, setBroken] = useState(() => new Set());

  const markBroken = useCallback((key) => {
    setBroken((previous) => {
      if (previous.has(key)) return previous;
      const next = new Set(previous);
      next.add(key);
      return next;
    });
  }, []);

  const pages = Math.max(1, Math.ceil(items.length / itemsPerView));

  const step = useCallback((delta) => {
    setPage((current) => {
      const next = Math.min(pages - 1, Math.max(0, current + delta));
      const track = trackRef.current;
      /*
        A page is the viewport *plus* the gap that follows it: `clientWidth`
        already holds the `itemsPerView - 1` gaps inside the page, not the one
        between the last card of this page and the first of the next. Stepping
        by `clientWidth` alone lands 8px short, and the shortfall accumulates —
        by page three the cards no longer line up with the view.
      */
      if (track) {
        track.scrollTo({ left: next * (track.clientWidth + CARD_GAP), behavior: 'smooth' });
      }
      return next;
    });
  }, [pages]);

  if (!items.length) return null;

  return (
    <div className={`flex min-w-0 flex-col gap-2 ${className}`.trim()}>
      <WidgetCarouselNav
        index={page}
        count={pages}
        onStep={step}
        previousLabel="Previous items"
        nextLabel="Next items"
        label={`${itemsPerView} per view · ${items.length} items`}
      />

      <div
        ref={trackRef}
        className="scrollbar-aimsos-auto flex min-w-0 gap-2 overflow-x-auto scroll-smooth"
      >
        {items.map((item) => {
          const showImage = item.imageUrl && !broken.has(item.key);

          const card = (
            <>
              <span className="relative block">
                {showImage ? (
                  <img
                    src={item.imageUrl}
                    alt=""
                    className="h-16 w-full rounded-md border border-edge object-cover"
                    loading="lazy"
                    onError={() => markBroken(item.key)}
                  />
                ) : (
                  <span className="flex h-16 w-full items-center justify-center rounded-md border border-edge bg-surface-muted">
                    <ImageOff size={16} className="text-content-faint" aria-hidden />
                  </span>
                )}
                {item.badge ? (
                  <span className="absolute left-1 top-1">
                    <Badge size="small" variant="neutral">{item.badge}</Badge>
                  </span>
                ) : null}
              </span>

              <span className="truncate text-xs font-semibold text-content">{item.title}</span>
              {item.subtitle ? (
                <span className="truncate text-[11px] text-content-muted">{item.subtitle}</span>
              ) : null}
            </>
          );

          const shell = 'flex min-w-0 shrink-0 flex-col gap-1.5 rounded-lg border border-edge p-2.5';
          const width = {
            flexBasis: `calc((100% - ${(itemsPerView - 1) * CARD_GAP}px) / ${itemsPerView})`,
          };

          return onItemClick ? (
            <button
              key={item.key}
              type="button"
              onClick={() => onItemClick(item.raw)}
              style={width}
              className={`${shell} text-left hover:bg-surface-hover`}
            >
              {card}
            </button>
          ) : (
            <div key={item.key} style={width} className={shell}>
              {card}
            </div>
          );
        })}
      </div>
    </div>
  );
}

CarouselWidget.propTypes = {
  data: PropTypes.shape({
    items: PropTypes.arrayOf(
      PropTypes.shape({
        row_key: PropTypes.string,
        title: PropTypes.string.isRequired,
        subtitle: PropTypes.string,
        badge: PropTypes.string,
        image_url: PropTypes.string,
      }),
    ).isRequired,
  }).isRequired,
  itemsPerView: PropTypes.number,
  onItemClick: PropTypes.func,
  className: PropTypes.string,
};

export default memo(CarouselWidget);
